import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import style from './Plan.module.scss';
import appStyle from '../App/App.module.scss';
import Collapse from '../Collapse/Collapse';
import classnames from 'classnames';
import QuestionMarkIcon from '../icons/QuestionMarkIcon';
import Check from '../icons/Check';
import Arrow from '../icons/Arrow';
import piggy_bank from '../../assets/images/piggy_bank.gif'
import arrows from '../../assets/images/arrows.gif'
import { getPriceBlock, preparePriceBlock } from '../Price';
import { getCostBlock } from '../Cost';
import { CurrentBadge } from '../CurrentBadge';
import DiscountBadge from '../DiscountBadge/DiscountBadge';
import { IPlan } from '../../model';
import { format, isValid } from 'date-fns';
import { AppContext } from '../../context/AppContext';

export interface IProps {
  plan: IPlan;
}

export const maxPlanHeightLarge = 440;
export const maxPlanHeightSmall = 360;

export const maxCollapseHeightLarge = 21;
export const maxCollapseHeightSmall = 13;

const Plan = ({plan}: IProps) => {
  const {
    yearly,
    maxCurrent,
    selectedOneTimeItem,
    oneTimeItem,
    baseCheckoutPath,
    showRenewPopup
  } = useContext(AppContext)
  const isLargeScreen = useMediaQuery({minWidth: 1601});
  const isSmallScreen = useMediaQuery({maxWidth: 1330});

  const maxPlanHeight = isLargeScreen ? maxPlanHeightLarge : maxPlanHeightSmall;

  const [isAdditional, setIsAdditional] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isShow, setIsShow] = useState(false);
  const node = useRef<HTMLDivElement>(null);

  const [height, setHeight] = useState<number>()
  const ref = useRef<HTMLDivElement>(null)

  const renderChildPlans = (childPlan: IPlan, showCollapse: boolean, toggleMain: (isOpen: boolean) => void, recalculateHeight: () => boolean) => {
    if (childPlan?.features.length) {
      return <li
        className={style.with_children}
        data-collapse={childPlan.plan_id}>
        <Collapse maxHeight={isSmallScreen ? maxCollapseHeightSmall : maxCollapseHeightLarge}>
          {(isOpenChildPlan, toggleOpenChildPlan, {recalculateHeight: recalculateHeightChildPlan}) => {
            return <>
              <div
                className={classnames('collapse__toggle', style.toggle, {[style.collapsed]: !isOpenChildPlan})}
                data-collapse={childPlan.plan_id}
                onClick={() => {
                  const newValue = !isOpenChildPlan;
                  toggleOpenChildPlan(newValue);
                  let mark = showCollapse;
                  setTimeout(() => {
                    const newShowCollapse = recalculateHeight();
                    if (newShowCollapse && !mark) toggleMain(true);
                    if (newValue) {
                      toggleMain(true)
                    }
                  }, 200)
                }}
              >
                <Check color={'#00AEFF'} className={style.check}/>
                {childPlan.display.name}
                <Arrow className={style.arrow}/>
              </div>
              <div className="collapse__content" data-collapse={childPlan.plan_id}>
                <div className={style.collapse__content_inner} data-collapse={childPlan.plan_id}>
                  {childPlan.childPlans && childPlan.childPlans.length
                    ? (
                      <ul className={style.subscription_features}>
                        {childPlan.childPlans.map((childPlan) => renderChildPlans(childPlan, showCollapse, (newValue) => {
                          let mark = showCollapse;
                          setTimeout(() => {
                            recalculateHeightChildPlan();
                            setTimeout(() => {
                              const newShowCollapse = recalculateHeight();
                              if (newShowCollapse && !mark) toggleMain(true);
                              if (newValue) {
                                toggleMain(true);
                                toggleOpenChildPlan(true);
                              }
                            }, 200)
                          }, 200)
                        }, recalculateHeight))}
                      </ul>
                    ) : ''}
                  <ul className={style.subscription_features}>
                    {childPlan.features.map(feature => <li><Check className={style.check}/>{feature}
                    </li>)}
                  </ul>
                </div>
              </div>
            </>
          }}
        </Collapse>
      </li>;
    }
    return <li><Check className={style.check}/>{childPlan?.display.name}</li>;
  }

  useEffect(() => {
    setIsLoading(true)
    if (ref.current) {
      setHeight(ref.current?.offsetHeight)
      setTimeout(() => {
        setIsLoading(false);
      }, 400)
    }
  }, [])

  const handleClickOutside = (e: { target: any; }) => {
    if (node.current && node.current.contains(e.target)) {
      return;
    }
    setIsShow(false);
  };

  useEffect(() => {
    if (isShow) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isShow]);

  useEffect(() => {
    setHeight(ref.current?.clientHeight);
  }, [isAdditional])

  let additionalCostBlock;

  const mainPlan = isAdditional ? plan.additional : plan;
  const currentPrice = mainPlan?.current?.price;

  const yearAmount = mainPlan?.current?.interval === 'year'
    ? currentPrice
      ? Number(currentPrice)
      : mainPlan?.prices.yearly.unit_amount
    : mainPlan?.prices.yearly.unit_amount;

  const monthAmount = mainPlan?.current?.interval === 'month'
    ? currentPrice
      ? Number(currentPrice)
      : mainPlan?.prices.monthly.unit_amount
    : mainPlan?.prices.monthly.unit_amount;

  const costBlock = getCostBlock({
    yearly,
    yearAmount: yearAmount,
    monthAmount: monthAmount
  })
  let additionalPlan = plan.additional;

  additionalCostBlock = getCostBlock({
    yearly,
    monthAmount: additionalPlan?.prices.monthly.unit_amount,
    prevMonthAmount: monthAmount,
    yearAmount: additionalPlan?.prices.yearly.unit_amount,
    prevYearAmount: yearAmount
  })
  if (isAdditional) additionalCostBlock = ''

  const maxHeight = maxPlanHeight - (height || 0) - 20;

  const downgrade = Boolean(maxCurrent && maxCurrent > (mainPlan?.plan_tier || 0));

  const isUpgradePossible = mainPlan?.current?.interval === 'month';
  const current = mainPlan?.current;

  const priceObjectCheck = mainPlan?.prices[yearly ? 'yearly' : 'monthly'];
  let showAdditional: boolean = true;
  if (typeof priceObjectCheck !== 'object') {
    if (isAdditional) {
      showAdditional = false;
    } else {
      if (!plan.additional) return <></>;
      if (!plan.additional?.prices?.[yearly ? 'yearly' : 'monthly']) return <></>;
      return <Plan plan={plan.additional}/>
    }
  } else {
    if (!isAdditional) {
      if (!plan.additional?.prices?.[yearly ? 'yearly' : 'monthly']) showAdditional = false;
    }
  }

  const priceObject = !yearly ? mainPlan?.prices.monthly : mainPlan?.prices.yearly;
  const canceledAtDate = new Date(Number(`${String(current?.subscription?.cancel_at)}000`))
  const subscriptionEndDate = new Date(Number(`${String(current?.subscription?.current_period_end)}000`))

  const currentBadge = CurrentBadge({plan, yearly, isAdditional});
  // @ts-ignore
  // const currentBadge = CurrentBadge({plan: {...plan, current: {interval: 'month'}}, yearly: false, isAdditional});

  // console.log('oneTimeItem: ', oneTimeItem);
  let discount
  if (oneTimeItem && oneTimeItem.subscriber_period_restriction === (yearly ? 'yearly' : 'monthly')) {
    discount = Math.round((1 - (oneTimeItem?.subscriber_price || 0) / (oneTimeItem?.base_price || 1)) * 100);

    const foundDiscount = oneTimeItem?.discounts?.find(discount => discount.plan_id === plan.plan_id);
    if (foundDiscount && (foundDiscount.period_restriction ? foundDiscount.period_restriction === (yearly ? 'yearly' : 'monthly') : true)) {
      discount = Math.round((1 - ((oneTimeItem?.subscriber_price || 0) - foundDiscount.amount_off) / (oneTimeItem?.base_price || 1)) * 100);
    }
  }

  return (
    <div className={style.plan_wrapper} style={(isLoading ? {maxHeight: maxPlanHeight + 2.2} : {})}>
      <div className={style.plan_space}/>
      {!downgrade && discount ? <DiscountBadge discount={discount}/> : ''}
      <div
        className={classnames(
          style.plan,
          {[style.plan_recommended]: !currentBadge && plan.plan_tier === 4 && plan.display.sport_category === 'all'},
          {[style.plan_current]: currentBadge},
          {[style.downgrade]: downgrade}
        )}
        data-plan-id={plan.plan_id}>

        {!currentBadge && plan.plan_tier === 4 && plan.display.sport_category === 'all'
          ? <div className={style.infobar}><h3>Best Value</h3></div>
          : ''}

        <div className={style.plan_top_part} ref={ref}>
          {currentBadge}
          <h4 className={style.title}>
            {plan.display.name} {isAdditional ? <> and <br/>{plan.additional?.display.name}</> : ''}
          </h4>
          {current
            ? (<>
              {(isUpgradePossible && currentPrice && (Number(currentPrice) > mainPlan?.prices?.yearly.unit_amount / 12)
                ? (yearly
                  ? <>
                    {costBlock}
                    <div className={style.upgrade}>
                      {selectedOneTimeItem
                        ? <a
                          href={`${baseCheckoutPath}?price_id=${mainPlan?.prices?.yearly?.price_id}${selectedOneTimeItem ? `&otp_ids=[${selectedOneTimeItem.id}]` : ''}`}
                          className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button, style.yellow)}>
                          <img src={piggy_bank} alt="" style={{width: 24, height: 24, margin: '-5px 5px 0 -15px'}}/>
                          Upgrade & Buy Almanac
                        </a>
                        : <a href={`${baseCheckoutPath}?price_id=${mainPlan?.prices?.yearly?.price_id}`}
                             className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button)}>
                          <img src={piggy_bank} alt="" style={{width: 24, height: 24, margin: '-5px 5px 0 -15px'}}/>
                          Upgrade to Annual Plan
                        </a>}

                    </div>
                  </>
                  : <div className={style.upgrade_annual}>
                    {currentPrice && (Number(currentPrice) * 12 > mainPlan?.prices?.yearly.unit_amount)
                      ? <div className={style.save_money}>save <div
                        className={style.price}>
                        {getPriceBlock(Number(currentPrice) * 12 - mainPlan?.prices?.yearly.unit_amount)}
                      </div>
                        <sub>/year</sub>
                      </div>
                      : ''
                    }
                    {selectedOneTimeItem
                      ? <a
                        href={`${baseCheckoutPath}?price_id=${mainPlan?.prices?.yearly?.price_id}${selectedOneTimeItem ? `&otp_ids=[${selectedOneTimeItem.id}]` : ''}`}
                        className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button)}>
                        Upgrade & Buy Almanac
                      </a>
                      : <a href={`${baseCheckoutPath}?price_id=${mainPlan?.prices?.yearly?.price_id}`}
                           className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button)}>
                        Upgrade to annual plan
                      </a>}
                    <div
                      className={style.payment_period}>billed annually ${mainPlan?.prices?.yearly.unit_amount / 100}
                    </div>
                  </div>)
                : (yearly ? '' : <div className={style.cost}>
                  <div className={style.price}>{getPriceBlock(Number(currentPrice))}</div>
                </div>))}
              <div className={style.cancel_row}>
                {(current.subscription?.cancel_at ? <div className={style.canceled}>Canceled</div> : '')}
                {(current.subscription?.cancel_at ? (canceledAtDate ? <>
                    <div className={style.next_payment}>
                      Active until {canceledAtDate.toDateString()}
                    </div>
                  </> : '')
                  : (subscriptionEndDate && isValid(subscriptionEndDate) ? <div className={style.next_payment}>
                    <span>Next payment {getPriceBlock(Number(current.price))} on {format(subscriptionEndDate, 'PP')}</span>
                    <div
                      // href={`${baseCheckoutPath}?price_id=${priceObject?.price_id}${selectedOneTimeItem ? `&otp_ids=[${selectedOneTimeItem.id}]` : ''}`}
                      className={classnames(style.renew_button)}
                      onClick={() => {
                        showRenewPopup({plan: plan})
                      }}
                    >
                      Renew Now
                    </div>
                  </div> : ''))}
              </div>
              {(current.subscription?.cancel_at ? (canceledAtDate ? <a
                href={`${baseCheckoutPath}?price_id=${priceObject?.price_id}`}
                className={classnames('button-primary button-primary_md')}
                style={{width: '100%', marginBottom: 20}}
              >
                Don't Cancel Subscription
              </a> : '') : '')}
            </>)
            : <>
              {costBlock}
              {!downgrade && selectedOneTimeItem ? <>
                {
                  <div className={style.cost}>
                    <s className={style.price} style={{color: '#837009', borderColor: '#837009'}}>{preparePriceBlock({
                      amount: selectedOneTimeItem.base_price
                    })}</s>
                    <div className={style.price} style={{color: '#FCD924'}}>
                      {preparePriceBlock({
                        amount: selectedOneTimeItem.subscriber_price - (selectedOneTimeItem?.discounts?.find(discount => discount.plan_id === plan.plan_id)?.amount_off || 0)
                      })}
                    </div>
                  </div>
                }
                <p className={style.one_time_label}>one time Almanac fee</p>
              </> : ''}

              {!downgrade
                ? <div className={classnames(style.upgrade, {[style.yellow]: !!selectedOneTimeItem})}>
                  <a
                    href={`${baseCheckoutPath}?price_id=${priceObject?.price_id}${selectedOneTimeItem ? `&otp_ids=[${selectedOneTimeItem.id}]` : ''}`}
                    className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button)}>
                    {maxCurrent && maxCurrent < plan.plan_tier
                      ? (selectedOneTimeItem ? <>Upgrade and Buy Almanac</> : <>
                        <img src={arrows} alt="" style={{width: 40, height: 40, margin: '0 -5px 0 -15px'}}/>
                        Upgrade
                      </>)
                      : (selectedOneTimeItem ? <>Subscribe and Buy Almanac</> : <>
                        {/*<img src={arrows} alt="" style={{width: 40, height: 40, margin: '0 -5px 0 -15px'}}/>*/}
                        Subscribe
                      </>)}
                  </a>
                </div>
                : ''
              }
            </>}

          {showAdditional ? <div className={style.additionalPlan_wrapper}>
            {plan.additional ? <div className={style.additionalPlan}>
              {'Add High Stakes +'.split('').map(i => i === ' ' ? <i>&nbsp;</i> : <span>{i}</span>)}<QuestionMarkIcon/>
              <div className={classnames(appStyle.switch, style.switch)}>
                <label className={classnames(appStyle.switch_wrapper, style.switch_wrapper)}>
                  <input type="checkbox" name="additional" checked={isAdditional}
                         onChange={() => setIsAdditional(!isAdditional)}/>
                  <span className={classnames(appStyle.switch_toggle, style.switch_toggle)}></span>
                </label>
              </div>

              <div className={style.additionalPlan_hover_card}>
                <div className={style.additionalPlan_hover_card_inner}>
                  <div className={style.additionalPlan_hover_card_header}>
                    <h3 className={style.additionalPlan_hover_card_title}>High Stakes+</h3>
                    <div className={style.additionalPlan_hover_card_billed}>
                      {additionalCostBlock}
                    </div>
                  </div>

                  <div className={style.features}>
                    <div className={style.features_title}>
                      Included:
                    </div>
                    {additionalPlan?.features?.length ? <ul className={style.subscription_features}>
                      {additionalPlan.features.map(feature => <li><Check className={style.check}/>{feature}</li>)}
                    </ul> : ''}
                  </div>
                </div>
              </div>
            </div> : ''}
          </div> : ''}
        </div>
        {plan.childPlans?.length || plan.features?.length ? (
          <Collapse maxHeight={maxHeight} key={plan.plan_id + isAdditional ? 'additional' : ''}>
            {(isOpen, toggleMain, {recalculateHeight, showCollapse}) => {
              return (<div className={style.features} style={{
                paddingBottom: 30
              }}>
                <div className={style.features_title}>
                  Included:
                </div>
                {plan.childPlans && plan.childPlans.length
                  ? (<ul className={style.subscription_features}>
                    {plan.childPlans.map((childPlan) => renderChildPlans(childPlan, showCollapse, toggleMain, recalculateHeight))}
                  </ul>) : ''}
                {isAdditional && plan.additional && plan.additional.features?.length ?
                  <ul className={style.subscription_features}>
                    {plan.additional.features.map(feature => <li><Check className={style.check}/>{feature}</li>)}
                  </ul> : ''}

                {plan.features?.length ? <ul className={style.subscription_features}>
                  {plan.features.map(feature => <li><Check className={style.check}/>{feature}</li>)}
                </ul> : ''}
                {showCollapse ? (isOpen
                    ? <div className={style.collapse_toggle} onClick={() => toggleMain(false)}>Less Features</div>
                    : <div className={style.collapse_toggle} onClick={() => toggleMain(true)}>Show More Features</div>
                ) : ''}
              </div>)
            }}
          </Collapse>
        ) : ''}
      </div>
      <div className={style.plan_space}/>
    </div>

  );
};

export default Plan;
